import { Injectable } from '@angular/core';

import { environment } from '../../../environments/environment';

/**
 * Injects the Google Maps JS API script once and exposes a single promise that
 * resolves only after the script (and its `places`/`geometry` libraries) has
 * actually finished loading. Consumers of `google.maps.places.*` should await
 * `whenReady()` before constructing any Places service, instead of assuming the
 * global `google` object is already available.
 */
@Injectable({ providedIn: 'root' })
export class GoogleMapsLoaderService {
  private loadPromise: Promise<void> | null = null;

  load(dom: Document): Promise<void> {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = new Promise((resolve, reject) => {
      const script = dom.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApiKey}&region=AU&v=weekly&libraries=places,geometry&loading=async`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = (err) => reject(err);
      dom.head.appendChild(script);
    });

    return this.loadPromise;
  }

  /**
   * Resolves once the Maps script has loaded. Rejects if the script failed to
   * load (e.g. blocked by an ad blocker/network issue) or hasn't been
   * requested via `load()` yet.
   */
  whenReady(): Promise<void> {
    if (!this.loadPromise) {
      return Promise.reject(new Error('Google Maps script has not been loaded yet.'));
    }
    return this.loadPromise;
  }
}
